import type { Engine } from "../../engine/Engine";

/**
 * Segment draw snap hissiyatı — dışarıdan ayarlanabilir.
 * zoom büyüdükçe (yakınlaşma) yarıçaplar küçülür; zoom out → yapışma alanı genişler (cap ile sınırlı).
 */
export interface DrawSnapSettings {
  /** Referans zoom'daki grid kesişimine yapışma yarıçapı (metre). */
  gridSnapRadius: number;
  /** Referans zoom'daki graph vertex yapışma yarıçapı (metre). */
  vertexSnapRadius: number;
  /** Referans zoom'daki graph edge / merkez eksen yapışma yarıçapı (metre). */
  edgeSnapRadius: number;
  /** Merkez eksen (start→end) snap yarıçapı; yoksa edgeSnapRadius kullanılır. */
  centerlineSnapRadius: number;
  /** Referans zoom'daki merkez eksen uç işareti yarıçapı (metre, dolgu + halka dış yarıçapı). */
  endpointMarkerRadius: number;
  /** Kenar uçlarında vertex rekabet — t ∈ (eps, 1-eps) gövde sayılır. */
  endpointEps: number;
  /** Açı snap adımı (derece) — 0°, 45°, 90°, … */
  angleStepDeg: number;
  /** Açı snap toleransı (derece). */
  angleToleranceDeg: number;
  /** Bu ortho zoom'da yarıçaplar aynen kullanılır (2D varsayılan ≈ 5). */
  zoomReference: number;
  /**
   * Ölçek: (zoomReference / zoom) ^ zoomPower
   * Küçük değer → zoom out'ta daha az genişleme.
   */
  zoomPower: number;
  /** zoom alt sınırı (ortho minZoom ile uyumlu). */
  minZoom: number;
  /** zoom out ile yarıçap en fazla bu kat artar. */
  maxSnapScale: number;
  /** Snap crosshair: merkezden eksen ucu (px). Zoom'dan bağımsız ekran boyutu. */
  crosshairArmHalfPx: number;
  /** Snap crosshair çizgi kalınlığı (px). */
  crosshairLineWidthPx: number;
  /** Snap'ten çıkış eşiği = giriş eşiği × bu çarpan (hysteresis). */
  snapReleaseScale: number;
}

export const drawSnapSettings: DrawSnapSettings = {
  gridSnapRadius: 0.1,
  vertexSnapRadius: 0.12,
  edgeSnapRadius: 0.02,
  centerlineSnapRadius: 0.12,
  endpointMarkerRadius: 0.07,
  endpointEps: 0.02,
  angleStepDeg: 45,
  angleToleranceDeg: 4,
  zoomReference: 5,
  zoomPower: 0.3,
  minZoom: 1,
  maxSnapScale: 1.5,
  crosshairArmHalfPx: 9,
  crosshairLineWidthPx: 2,
  snapReleaseScale: 1.5,
};

export function resolveDrawSnapZoom(engine: Engine | null | undefined): number {
  const camera = engine?.activeCamera as { fov?: number } | undefined;
  const zoom = camera?.fov;
  if (typeof zoom === "number" && zoom > 0) return zoom;
  return drawSnapSettings.zoomReference;
}

/** zoom out → >1 (maxSnapScale ile sınırlı), zoom in → <1 (referans zoom'da 1). */
export function drawSnapZoomScale(zoom: number): number {
  const { zoomReference, zoomPower, minZoom, maxSnapScale } = drawSnapSettings;
  const z = Math.max(zoom, minZoom);
  const scale = Math.pow(zoomReference / z, zoomPower);
  return Math.min(scale, maxSnapScale);
}

export function scaledGridSnapRadius(zoom: number): number {
  return drawSnapSettings.gridSnapRadius * drawSnapZoomScale(zoom);
}

export function scaledVertexSnapRadius(zoom: number): number {
  return drawSnapSettings.vertexSnapRadius * drawSnapZoomScale(zoom);
}

export function scaledEdgeSnapRadius(zoom: number): number {
  return drawSnapSettings.edgeSnapRadius * drawSnapZoomScale(zoom);
}

export function scaledCenterlineSnapRadius(zoom: number): number {
  return drawSnapSettings.centerlineSnapRadius * drawSnapZoomScale(zoom);
}

export function scaledEndpointMarkerRadius(zoom: number): number {
  return drawSnapSettings.endpointMarkerRadius * drawSnapZoomScale(zoom);
}

/** Snap crosshair kol uzunluğu (world metre) — yalnızca `crosshairArmHalfPx` ile ayarlanır. */
export function crosshairArmWorldLength(
  engine: Engine | null | undefined,
): number {
  const px = drawSnapSettings.crosshairArmHalfPx;
  const camera = engine?.activeCamera?.instance;
  const canvas = engine?.canvas;
  if (!camera || !canvas) return px * 0.001;

  const height = canvas.clientHeight || 1;

  if (camera.type === "OrthographicCamera") {
    const ortho = camera as import("three").OrthographicCamera;
    const worldHeight = (ortho.top - ortho.bottom) / ortho.zoom;
    return (px / height) * worldHeight;
  }

  if (camera.type === "PerspectiveCamera") {
    const persp = camera as import("three").PerspectiveCamera;
    const target = (persp.userData as { controller?: { target?: { y: number } } })
      ?.controller?.target;
    const dy = target ? persp.position.y - target.y : persp.position.y;
    const dist = Math.max(Math.abs(dy), 0.001);
    const worldHeight = 2 * Math.tan((persp.fov * Math.PI) / 360) * dist;
    return (px / height) * worldHeight;
  }

  return px * 0.001;
}

/**
 * Align guide yarı uzunluğu (world metre) — viewport’u aşacak kadar büyük,
 * “sonsuz” H/V noktalı çizgi için.
 */
export function alignmentGuideHalfLength(
  engine: Engine | null | undefined,
): number {
  const camera = engine?.activeCamera?.instance;
  const canvas = engine?.canvas;
  if (!camera || !canvas) return 50;

  const width = canvas.clientWidth || 1;
  const height = canvas.clientHeight || 1;
  const aspect = width / height;

  let worldHeight: number;
  if (camera.type === "OrthographicCamera") {
    const ortho = camera as import("three").OrthographicCamera;
    worldHeight = (ortho.top - ortho.bottom) / ortho.zoom;
  } else if (camera.type === "PerspectiveCamera") {
    const persp = camera as import("three").PerspectiveCamera;
    const target = (persp.userData as { controller?: { target?: { y: number } } })
      ?.controller?.target;
    const dy = target ? persp.position.y - target.y : persp.position.y;
    const dist = Math.max(Math.abs(dy), 0.001);
    worldHeight = 2 * Math.tan((persp.fov * Math.PI) / 360) * dist;
  } else {
    return 50;
  }

  const worldWidth = worldHeight * aspect;
  // Köşegen yarıçapı + pay → ekranı her yerde aşar.
  return 0.5 * Math.hypot(worldWidth, worldHeight) * 1.25;
}
